import moment from 'moment'

import { IntervalUnit, ReportAccount, ReportAd, ReportCampaign, ReportCampaignDimension, CampaignDetailReport } from 'api/interfaces'
import { defaultTransformFn, platformApi } from 'api/rtkQueryApi/platform/platformApi'

export interface IGetReportCampaignQuery {
  accountId: string
  campaignIds?: string[]
  start: moment.Moment
  end: moment.Moment
  breakDownBy: IntervalUnit
  isAgency?: boolean
}

export interface IGetReportAdQuery {
  accountId: string
  campaignIds?: string[]
  start: moment.Moment
  end: moment.Moment
  isAgency?: boolean
}

export interface IGetReportCampaignDimensionQuery {
  accountId: string
  campaignIds?: string[]
  dimension: string
  breakDownBy: IntervalUnit
  start: moment.Moment
  end: moment.Moment
  isAgency?: boolean
}

export interface IGetReportAccountQuery {
  accountId: string
  start: moment.Moment
  end: moment.Moment
  isAgency?: boolean
}

export interface IGetCampaignDetailReportQuery {
  accountId: string
  campaignId: string
  start: moment.Moment
  end: moment.Moment
  isAgency?: boolean
}

const reportsTags = platformApi.enhanceEndpoints({
  addTagTypes: ['ReportCampaign', 'ReportAd', 'ReportCampaignDimension', 'ReportAccount', 'CampaignDetailReport']
})

export const reportsApi = reportsTags.injectEndpoints({
  endpoints: build => ({
    getReportCampaign: build.query<ReportCampaign[], IGetReportCampaignQuery>({
      query: ({ accountId, campaignIds, start, end, breakDownBy, isAgency }) => {
        const params = new URLSearchParams({
          start: start.format('YYYY-MM-DD'),
          end: end.format('YYYY-MM-DD'),
          breakDownBy: breakDownBy.toString()
        })

        if (campaignIds && campaignIds.length > 0) {
          campaignIds.forEach(id => params.append('campaignId', id))
        }

        return {
          url: `reports/${accountId}/campaigns?${params.toString()}`,
          method: 'GET',
          isContext: true,
          isAgency
        }
      },
      transformResponse: defaultTransformFn,
      providesTags: (result, error, { accountId, campaignIds }) => [
        {
          type: 'ReportCampaign',
          id: `${accountId}-${campaignIds?.join(',') || 'all'}`
        }
      ]
    }),

    getReportAd: build.query<ReportAd[], IGetReportAdQuery>({
      query: ({ accountId, campaignIds, start, end, isAgency }) => {
        const params = new URLSearchParams({
          start: start.format('YYYY-MM-DD'),
          end: end.format('YYYY-MM-DD')
        })

        if (campaignIds && campaignIds.length > 0) {
          campaignIds.forEach(id => params.append('campaignId', id))
        }

        return {
          url: `reports/${accountId}/ads?${params.toString()}`,
          method: 'GET',
          isContext: true,
          isAgency
        }
      },
      transformResponse: defaultTransformFn,
      providesTags: (result, error, { accountId, campaignIds }) => [
        {
          type: 'ReportAd',
          id: `${accountId}-${campaignIds?.join(',') || 'all'}`
        }
      ]
    }),

    getReportCampaignDimension: build.query<ReportCampaignDimension[], IGetReportCampaignDimensionQuery>({
      query: ({ accountId, campaignIds, dimension, breakDownBy, start, end, isAgency }) => {
        const params = new URLSearchParams({
          breakDownBy: breakDownBy.toString(),
          start: start.format('YYYY-MM-DD'),
          end: end.format('YYYY-MM-DD')
        })

        if (campaignIds && campaignIds.length > 0) {
          campaignIds.forEach(id => params.append('campaignId', id))
        }

        return {
          url: `reports/${accountId}/campaigns/dimension/${dimension.toLowerCase()}?${params.toString()}`,
          method: 'GET',
          isContext: true,
          isAgency
        }
      },
      transformResponse: defaultTransformFn,
      providesTags: (result, error, { accountId, campaignIds, dimension }) => [
        {
          type: 'ReportCampaignDimension',
          id: `${accountId}-${dimension}-${campaignIds?.join(',') || 'all'}`
        }
      ]
    }),

    getReportAccount: build.query<ReportAccount[], IGetReportAccountQuery>({
      query: ({ accountId, start, end, isAgency }) => {
        const params = new URLSearchParams({
          start: start.format('YYYY-MM-DD'),
          end: end.format('YYYY-MM-DD')
        })

        return {
          url: `reports/${accountId}/accounts?${params.toString()}`,
          method: 'GET',
          isContext: true,
          isAgency
        }
      },
      transformResponse: defaultTransformFn,
      providesTags: (result, error, { accountId }) => [
        { type: 'ReportAccount', id: accountId }
      ]
    }),
    getCampaignDetailReport: build.query<CampaignDetailReport, IGetCampaignDetailReportQuery>({
      query: ({ accountId, campaignId, start, end, isAgency }) => {
        const params = new URLSearchParams({
          start: start.format('YYYY-MM-DD'),
          end: end.format('YYYY-MM-DD')
        })

        return {
          url: `reports/${accountId}/campaigns/${campaignId}/detailed?${params.toString()}`,
          method: 'GET',
          isContext: true,
          isAgency
        }
      },
      transformResponse: defaultTransformFn,
      providesTags: (result, error, { accountId, campaignId }) => [
        { type: 'CampaignDetailReport', id: `${accountId}-${campaignId}` }
      ]
    })
  }),
  overrideExisting: false
})

export const {
  useGetReportCampaignQuery,
  useGetReportAdQuery,
  useGetReportCampaignDimensionQuery,
  useGetReportAccountQuery,
  useLazyGetReportCampaignQuery,
  useLazyGetReportAdQuery,
  useLazyGetReportCampaignDimensionQuery,
  useLazyGetReportAccountQuery,
  useGetCampaignDetailReportQuery,
  useLazyGetCampaignDetailReportQuery
} = reportsApi
